import {
  IOfferSchema,
  IHouseBTIDataSchema,
  IBuildingSchema,
  IUserSchema,
} from '@cian/api-models/offer-card/v1/get-offer-data';
import { plural } from '@cian/utils';
import { isNil } from 'ramda';
import * as React from 'react';

import {
  getHouseMaterialType,
  getOverlapType,
  getHeatingType,
  getGasSupplyType,
  getParkingType,
  getHasLabel,
} from './helpers';
import * as styles from './index.css';
import { getOfferType, isUrban } from '../../utils/offer_type';
import { SubscriptionButton } from '../subscription_address/experiments/subscription_button/subscription_button';
import { SubscriptionPopup } from '../subscription_address/subscription_popup';
import { ICreateSubscriptionRequest } from '../../repositories/monolithPython/v1/create-subscription';

export interface IBtiHouseDataStoreProps {
  houseData: IHouseBTIDataSchema | null;
  jkName: string;
  roomCount: number | null;
  offer: IOfferSchema;

  isLayoutSubscribeAddressExperiment: boolean;
  isOpen: boolean;
  isLoad: boolean;
  isSubscribe: boolean;
  globalError?: string;
  user: IUserSchema | null;
  pushSubscribed: boolean;
}

export interface IBtiHouseDataStoreDispatchProps {
  setVisible(isOpen: boolean): void;
  fetchCreateSubscription(parameters: ICreateSubscriptionRequest): Promise<void>;
}

export type TBtiHouseDataProps = IBtiHouseDataStoreProps & IBtiHouseDataStoreDispatchProps;

export class BtiHouseData extends React.Component<TBtiHouseDataProps, {}> {
  public render() {
    const {
      houseData,
      offer,
      isLayoutSubscribeAddressExperiment,
      setVisible,
      fetchCreateSubscription,
      isOpen,
      isLoad,
      isSubscribe,
      globalError,
      user,
      pushSubscribed,
    } = this.props;
    const { building } = offer;

    const offerType = getOfferType(offer);
    const isOfferUrban = isUrban(offerType);

    // bti
    const entrances = houseData && houseData.entrances;
    const isEmergency = houseData && !isNil(houseData.isEmergency) ? houseData.isEmergency : null;
    const seriesName = houseData && houseData.seriesName;
    const yearRelease = houseData && houseData.yearRelease || null;
    const houseOverlapType = houseData && houseData.houseOverlapType
      && getOverlapType(houseData.houseOverlapType);
    const houseHeatSupplyType = houseData && houseData.houseHeatSupplyType
      && getHeatingType(houseData.houseHeatSupplyType);
    const houseGasSupplyType = houseData && houseData.houseGasSupplyType
      && getGasSupplyType(houseData.houseGasSupplyType);

    // user provided
    const parkingType = building && building.parking && building.parking.type && getParkingType(building.parking.type);
    const garbageChute = getHasLabel(building && building.hasGarbageChute);
    const serviceElevatorsRaw = building && building.cargoLiftsCount;
    const elevatorsRaw = building && building.passengerLiftsCount;
    const elevators = [
      elevatorsRaw && `${elevatorsRaw} ${plural(elevatorsRaw, ['пассажирский', 'пассажирских', 'пассажирских'])}`,
      serviceElevatorsRaw && `${serviceElevatorsRaw} ${plural(serviceElevatorsRaw, [
        'грузовой', 'грузовых', 'грузовых'])}`,
    ].filter(Boolean).join(', ') || null;
    const hasGas = getHasLabel(offer.hasGas);

    // AI
    let houseMaterialType = findHouseMaterialType(houseData, building);

    const isBtiDataUnavailable = !entrances
      && isEmergency === null
      && !seriesName
      && !yearRelease
      && !houseMaterialType
      && !houseOverlapType
      && !houseHeatSupplyType
      && !houseGasSupplyType;

    const isUserProvidedDataUnavailable = !parkingType
      && !elevators
      && garbageChute === null
      && hasGas === null;

    if (isUserProvidedDataUnavailable && isBtiDataUnavailable) {
      return null;
    }

    return (
      <div className={styles['container']}>
        <div className={styles['title']}>
          <span>
            {
              this.props.jkName
                ? `ЖК \u00ab${this.props.jkName}\u00bb`
                : 'О доме'
            }
          </span>
          {!isBtiDataUnavailable && <span className={styles['note']}>По данным ЦИАН</span>}
        </div>
        {
          isLayoutSubscribeAddressExperiment &&
            <>
              <SubscriptionButton setVisible={setVisible} />
              <SubscriptionPopup
                fetchCreateSubscription={fetchCreateSubscription}
                offer={offer}
                isOpen={isOpen}
                isLoad={isLoad}
                isSubscribe={isSubscribe}
                globalError={globalError}
                onClose={() => setVisible(false)}
                isAuthenticated={Boolean(user && user.isAuthenticated)}
                user={user}
                pushSubscribed={pushSubscribed}
              />
            </>
        }
        <div className={styles['content']}>
          <div className={styles['column']}>
            {yearRelease && <div className={styles['item']}>
              <div className={styles['name']}>Год постройки</div>
              <div className={styles['value']}>{yearRelease}</div>
            </div>}
            {seriesName && <div className={styles['item']}>
              <div className={styles['name']}>Строительная серия</div>
              <div className={styles['value']}>{seriesName}</div>
            </div>}
            {houseMaterialType && <div className={styles['item']}>
              <div className={styles['name']}>Тип дома</div>
              <div className={styles['value']}>{houseMaterialType}</div>
            </div>}
            {houseOverlapType && <div className={styles['item']}>
              <div className={styles['name']}>Тип перекрытий</div>
              <div className={styles['value']}>{houseOverlapType}</div>
            </div>}
            {entrances && <div className={styles['item']}>
              <div className={styles['name']}>Подъезды</div>
              <div className={styles['value']}>{entrances}</div>
            </div>}
            {
              isOfferUrban &&
                <div className={styles['item']}>
                  <div className={styles['name']}>Лифты</div>
                  <div className={styles['value']}>{elevators || 'Нет'}</div>
                </div>
            }
            {houseHeatSupplyType && <div className={styles['item']}>
              <div className={styles['name']}>Отопление</div>
              <div className={styles['value']}>{houseHeatSupplyType}</div>
            </div>}
            {isEmergency !== null && <div className={styles['item']}>
              <div className={styles['name']}>Аварийность</div>
              <div className={styles['value']}>{isEmergency ? 'Да' : 'Нет'}</div>
            </div>}
            {parkingType && <div className={styles['item']}>
              <div className={styles['name']}>Парковка</div>
              <div className={styles['value']}>{parkingType}</div>
            </div>}
            {garbageChute !== null && <div className={styles['item']}>
              <div className={styles['name']}>Мусоропровод</div>
              <div className={styles['value']}>{garbageChute}</div>
            </div>}
            {(houseGasSupplyType || hasGas) && <div className={styles['item']}>
              <div className={styles['name']}>Газоснабжение</div>
              <div className={styles['value']}>{houseGasSupplyType || hasGas}</div>
            </div>}
          </div>
        </div>
      </div>
    );
  }
}

/* istanbul ignore next */
function findHouseMaterialType(houseData: IHouseBTIDataSchema | null, building: IBuildingSchema | null): string | null {
  if (houseData && houseData.houseMaterialType) {
    const houseMaterialType = houseData.houseMaterialType;

    if (houseMaterialType !== 'none' && houseMaterialType !== 'custom') {
      return getHouseMaterialType(houseMaterialType);
    }
  } else if (building && building.materialType) {
    const materialType = building.materialType as string;

    if (materialType !== 'none' && materialType !== 'custom') {
      return getHouseMaterialType(building.materialType);
    }
  }

  return null;
}
